import { apisStore } from "context/GlobalStates";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { useMemo, useState } from "react";
import moment from "moment-timezone";
import cronstrue from "cronstrue";
import {JobInfoDTO, JobsApi, Schedule} from "../../../serviceapi/jobRunnerApi";

interface ScheduleDialogProps {
  isVisible: boolean;
  setIsVisible: (value: boolean) => void;
  job: JobInfoDTO;
  updateCurrentJob: (job: JobInfoDTO) => void;
}

const ScheduleDialog = ({
  isVisible,
  setIsVisible,
  job,
  updateCurrentJob,
}: ScheduleDialogProps) => {
  const [currentSchedule, setCurrentSchedule] = useState<Schedule>(
    job.schedule
  );
  const apiJobs = apisStore.getState()?.jobRunner?.jobsApi as JobsApi;

  const timeZones = moment.tz.names();

  const cronValidation = useMemo(() => {
    const cron = currentSchedule.cron.trim();
    if (!cron) return { valid: false, description: "", error: "Cron expression is required." };

    const parts = cron.split(" ");
    if (parts.length !== 7) {
      return { valid: false, description: "", error: "Quartz cron requires 7 fields: sec min hour day month weekday year." };
    }

    try {
      const unixLikeCron = parts.slice(0, 6).join(" ");
      const description = cronstrue.toString(unixLikeCron, {
        use24HourTimeFormat: true,
        verbose: false,
        throwExceptionOnParseError: true,
        dayOfWeekStartIndexZero: false,
      });
      return { valid: true, description, error: "" };
    } catch {
      return { valid: false, description: "", error: "Invalid cron expression." };
    }
  }, [currentSchedule.cron]);

  const handleOpenCronGenerator = () => {
    window.open(
      "https://www.freeformatter.com/cron-expression-generator-quartz.html"
    );
  };

  const handleSave = async () => {
    await apiJobs.jobrunnerJobsUpdateScheduleIdPut(job.taskId, currentSchedule);
    updateCurrentJob({ ...job, schedule: currentSchedule });
    setIsVisible(false);
  };

  const handleFooterTemplate = () => {
    return (
      <div className="flex justify-content-end gap-2">
        <Button
          label="Cancel"
          className="p-button-text"
          onClick={() => setIsVisible(false)}
        />
        <Button
          label="Save"
          className="p-button-text mr-0"
          onClick={handleSave}
          disabled={!cronValidation.valid}
        />
      </div>
    );
  };

  return (
    <Dialog
      visible={isVisible}
      style={{ width: "20vw" }}
      header="Edit Schedule"
      footer={handleFooterTemplate}
      modal={false}
      onHide={() => setIsVisible(false)}
    >
      <div className="flex flex-column gap-5">
        <div className="flex flex-column w-12 gap-2">
          <label htmlFor="cron">Cron Expression</label>
          <InputText
            id="cron"
            value={currentSchedule.cron}
            className={!cronValidation.valid && currentSchedule.cron ? "p-invalid" : ""}
            onChange={(e) =>
              setCurrentSchedule({ ...currentSchedule, cron: e.target.value })
            }
          />
          {cronValidation.error && currentSchedule.cron && (
            <small className="p-error">{cronValidation.error}</small>
          )}
          {cronValidation.valid && (
            <small style={{ color: "var(--green-400)" }}>
              {cronValidation.description}
            </small>
          )}
        </div>
        <div className="flex flex-column w-12 gap-2">
          <label htmlFor="timeZone">Time Zone</label>
          <Dropdown
            id="timeZone"
            value={currentSchedule.timeZone}
            filter
            options={timeZones.map((tz) => ({ label: tz, value: tz }))}
            onChange={(e) =>
              setCurrentSchedule({ ...currentSchedule, timeZone: e.value })
            }
          />
        </div>
        <Button
          icon="pi pi-cog"
          label="Cron Generator"
          className="p-button-text scheduler-button w-10rem"
          onClick={handleOpenCronGenerator}
          tooltip="Open Cron Generator"
          tooltipOptions={{ position: "bottom" }}
        />
      </div>
    </Dialog>
  );
};

export default ScheduleDialog;
